import { useEffect, useState } from 'react';

/** How the entry flash stands: not shown, washing the screen white, or fading off to reveal the run. */
export type EntryFlashPhase = 'none' | 'in' | 'out';

/** How long the flash takes to fade off once the run shows (or the start is called off); matches the stylesheet. */
export const ENTRY_FLASH_OUT_MS = 800;

/**
 * The white flash the party enters a Crucible through (#266): when the entry
 * countdown reaches zero the screen washes white, with the entrance's flare,
 * and holds while the run starts; once the run shows (its loading screen)
 * the white fades off. If the start is called off first, it fades off over
 * the gathering.
 *
 * Kept by the crucible page, which shows both the gathering and the run, so
 * the flash carries across the hand-off.
 */
export function useEntryFlash(isEntering: boolean, isRunShowing: boolean): EntryFlashPhase {
    const [phase, setPhase] = useState<EntryFlashPhase>('none');

    useEffect(() => {
        if (isEntering && !isRunShowing) {
            setPhase('in');
            return;
        }
        setPhase((current) => (current === 'in' ? 'out' : current));
    }, [isEntering, isRunShowing]);

    useEffect(() => {
        if (phase !== 'out') {
            return undefined;
        }
        const timer = setTimeout(() => setPhase('none'), ENTRY_FLASH_OUT_MS);
        return () => clearTimeout(timer);
    }, [phase]);

    return phase;
}
